/**
 * Довідник умов купівлі-продажу — реєстр (етап 2 модуля «Фінансовий результат»).
 *
 * Таблиця записів із фільтрами (об'єкт, статус) і бічною формою (drawer) для
 * додавання/редагування. Права на запис — ролі «Оператор»/«CTO» (перевірка все
 * одно робиться сервером). Видаляти можна лише чернетки; чинні умови завершуються.
 *
 * РЕДИЗАЙН: розділ приведено до спільної дизайн-системи кабінету — токени
 * замість власної палітри useFinTheme (там pageBg #111113 проти --bg #28323C,
 * через що розділ читався як окремий застосунок), скляна .tile, Urbanist,
 * Lucide, контроли 36/10. Фільтри переїхали в топбар (§6, useSaleTermsUi).
 * Дуального режиму немає навмисно: пункт меню під гейтом
 * VITE_FEATURE_FINANCIAL_RESULT, тож у прод-збірці розділ відсутній — так само
 * зроблено з /dispatch/forecast-mms.
 *
 * Логіку, ендпоїнти й перевірку ролей не змінено.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, authFetch } from '@/services/api';
import { getStoredUser } from '@/hooks/useAuth';
import { useSaleTermsUi } from '@/hooks/useSaleTermsUi';
import {
  Archive, Check, CheckCircle2, Pencil, Plus, Trash2, TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import {
  type SaleTerm, type AssetLite,
  BUYER_TYPES, SALE_TYPES, PRICE_MODELS, STATUSES, labelOf, dmy,
} from '../theme';
import SaleTermsDrawer from './SaleTermsDrawer';

const num = (v: number | string | null | undefined) =>
  v == null || v === '' ? '—' : Number(v).toLocaleString((i18n.language === 'en' ? 'en-GB' : 'uk-UA'), { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Статуси — за догмою §3: чинний → --ok, завершений → нейтральний сірий,
 * чернетка → приглушений. Раніше тут був яскравий #22C55E і два майже
 * однакові сірі (#8A8A93 / #6B7280), які не розрізнялись.
 */
const STATUS_COLOR: Record<string, string> = {
  draft: 'var(--text-dim)',
  active: 'var(--ok)',
  completed: 'var(--text-muted)',
};

export default function SaleTermsPage() {
  const { t } = useTranslation();
  // Ті самі ролі, що й FINANCIAL_EDIT_ROLES на бекенді. Кнопки лише ховаються —
  // справжня перевірка все одно робиться сервером на кожен запис.
  const canEdit = useMemo(() => {
    const role = getStoredUser()?.role;
    return role === 'Оператор' || role === 'CTO' || role === 'Фінансовий директор';
  }, []);

  const ui = useSaleTermsUi();
  const { asset: fAsset, status: fStatus, setAssets: pushAssets } = ui;

  const [items, setItems] = useState<SaleTerm[]>([]);
  const [assets, setAssetsLocal] = useState<AssetLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [drawer, setDrawer] = useState<{ open: boolean; record: SaleTerm | null }>({ open: false, record: null });
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Видалення підтверджується другим натисканням на ту саму кнопку — замість
  // window.confirm, який ігнорував усю дизайн-систему (прийом із MmsJournal).
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const loadAssets = useCallback(() => {
    authFetch(`${API_BASE_URL}/assets`).then((r) => (r.ok ? r.json() : [])).then((d: any) => {
      const list = Array.isArray(d) ? d : (d.data ?? d.stations ?? []);
      const mapped = list.map((a: any) => ({ id: String(a.id), assetName: a.assetName ?? a.asset_name ?? a.name }));
      setAssetsLocal(mapped);
      pushAssets(mapped);       // топбар малює опції з цього ж списку
    }).catch(() => { setAssetsLocal([]); pushAssets([]); });
  }, [pushAssets]);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (fAsset) q.set('assetId', fAsset);
    if (fStatus) q.set('status', fStatus);
    authFetch(`${API_BASE_URL}/financial/sale-terms?${q.toString()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setItems(d.items ?? []))
      .catch(() => setItems([]))
      .finally(() => { setLoading(false); setLoaded(true); });
  }, [fAsset, fStatus]);

  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setConfirmDel(null); }, [fAsset, fStatus]);

  // Зміна статусу відправляє УВЕСЬ запис (PUT перезаписує поля), лише статус інший.
  const patchStatus = async (r: SaleTerm, status: string) => {
    setBusyId(r.id); setMsg(null);
    try {
      const body = { ...r, status,
        contract_date: r.contract_date ? String(r.contract_date).slice(0, 10) : null,
        valid_from: String(r.valid_from).slice(0, 10), valid_to: String(r.valid_to).slice(0, 10),
        green_tariff_until: r.green_tariff_until ? String(r.green_tariff_until).slice(0, 10) : null,
      };
      const resp = await authFetch(`${API_BASE_URL}/financial/sale-terms/${r.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.message || `HTTP ${resp.status}`);
      setMsg({ kind: 'ok', text: status === 'active' ? t('ak_fin2_term_activated') : t('ak_fin2_term_completed') });
      load();
    } catch (e: any) { setMsg({ kind: 'err', text: e?.message || t('ak_fin2_failed_to_change_the_status') }); }
    finally { setBusyId(null); }
  };

  const remove = async (r: SaleTerm) => {
    setBusyId(r.id); setMsg(null);
    try {
      const resp = await authFetch(`${API_BASE_URL}/financial/sale-terms/${r.id}`, { method: 'DELETE' });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.message || `HTTP ${resp.status}`);
      setConfirmDel(null);
      setMsg({ kind: 'ok', text: t('ak_fin2_draft_deleted') });
      load();
    } catch (e: any) { setMsg({ kind: 'err', text: e?.message || t('ak_fin2_failed_to_delete') }); }
    finally { setBusyId(null); }
  };

  const statusPill = (s: string) => {
    const st = STATUSES.find((x) => x.value === s);
    const c = STATUS_COLOR[s] ?? 'var(--text-muted)';
    return (
      <span className="text-[10.5px] font-semibold px-2 py-0.5 whitespace-nowrap"
        style={{ borderRadius: 999, color: c, border: `1px solid ${c}`,
                 background: `color-mix(in srgb, ${c} 12%, transparent)` }}>
        {st?.label ?? s}
      </span>
    );
  };

  const priceCell = (r: SaleTerm) => {
    if (r.price_model === 'fixed') return t('ak_fin2_uah_mwh', { v: num(r.fixed_price_uah_mwh) });
    const base = r.base_index === 'rdn' ? t('ak_fin2_dam') : t('ak_fin2_index');
    const p = r.markup_pct == null ? '' : Number(r.markup_pct);
    const sign = typeof p === 'number' ? (p >= 0 ? '+' : '−') : '';
    return `${base} ${sign}${typeof p === 'number' ? Math.abs(p) : ''}%`;
  };

  const th = {
    padding: '11px 12px', fontSize: 12, fontWeight: 500, textAlign: 'left' as const,
    color: 'var(--text-muted)', background: 'var(--surface-2)', whiteSpace: 'nowrap' as const,
    borderBottom: '1px solid var(--line)',
  };
  const td = {
    padding: '13px 12px', fontSize: 12.5, color: 'var(--text)', whiteSpace: 'nowrap' as const,
    borderTop: '1px solid var(--line)',
  };

  /** Дія в рядку — квадратна пілюля 28×28 за стандартом контролів. */
  const iconBtn = (Icon: LucideIcon, title: string, onClick: () => void, disabled = false, color?: string) => (
    <button title={title} onClick={onClick} disabled={disabled || busyId != null}
      className="inline-flex items-center justify-center cursor-pointer"
      style={{ width: 28, height: 28, borderRadius: 8, color: color ?? 'var(--text-muted)',
               background: 'var(--surface-2)', border: '1px solid var(--line)',
               opacity: disabled ? 0.4 : 1 }}>
      <Icon size={14} strokeWidth={2} />
    </button>
  );

  const firstLoad = loading && !loaded;

  return (
    <DashboardLayout breadcrumb={[t('ak_fin2_terms_directory')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо: назву розділу показує топбар.
              Лишається лише функціональна кнопка додавання. */}
          <div className="mb-3 flex justify-end">
            {canEdit && (
              <button onClick={() => setDrawer({ open: true, record: null })}
                title={t('ak_fin2_add_term')} aria-label={t('ak_fin2_add_term')}
                className="inline-flex items-center justify-center cursor-pointer shrink-0"
                style={{ width: 36, height: 36, borderRadius: 10, color: 'var(--text)',
                         background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
                <Plus size={16} strokeWidth={2} />
              </button>
            )}
          </div>

          {msg && (
            <div className="tile p-3 mb-3 text-[12px] flex items-start gap-2"
              style={{ color: msg.kind === 'ok' ? 'var(--ok)' : 'var(--err)' }}>
              {msg.kind === 'ok'
                ? <CheckCircle2 size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
                : <TriangleAlert size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />}
              <span>{msg.text}</span>
            </div>
          )}

          <div className="tile overflow-hidden">
            <div className="overflow-x-auto">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                <thead>
                  <tr>
                    <th style={th}>{t('ak_fin2_asset')}</th><th style={th}>{t('ak_fin2_seller')}</th><th style={th}>{t('ak_fin2_buyer')}</th>
                    <th style={th}>{t('ak_fin2_sale_type')}</th><th style={th}>{t('ak_fin2_model')}</th><th style={th}>{t('ak_fin2_price_formula')}</th>
                    <th style={th}>{t('ak_fin2_validity_period')}</th><th style={th}>{t('ak_fin2_vat')}</th><th style={th}>{t('ak_fin2_status')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('ak_fin2_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {firstLoad ? (
                    <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>{t('ak_fin2_loading')}</td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>
                      {t('ak_fin2_no_terms_yet')} {canEdit ? t('ak_fin2_press_add_term') : ''}
                    </td></tr>
                  ) : items.map((r) => {
                    const confirming = confirmDel === r.id;
                    return (
                      <tr key={r.id}>
                        <td style={{ ...td, fontWeight: 600 }}>{r.asset_name || r.asset_id}</td>
                        <td style={td}>{r.seller_name || '—'}</td>
                        <td style={td}>{r.buyer_name || labelOf(BUYER_TYPES, r.buyer_type)}</td>
                        <td style={td}>{labelOf(SALE_TYPES, r.sale_type)}</td>
                        <td style={td}>{labelOf(PRICE_MODELS, r.price_model)}</td>
                        <td style={td}>{priceCell(r)}</td>
                        <td style={{ ...td, color: 'var(--text-muted)' }}>{dmy(r.valid_from)} – {dmy(r.valid_to)}</td>
                        <td style={{ ...td, color: 'var(--text-muted)' }}>{r.vat_included ? t('ak_fin2_incl_vat') : t('ak_fin2_excl_vat')}</td>
                        <td style={td}>{statusPill(r.status)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <div className="inline-flex items-center gap-1.5">
                            {iconBtn(Pencil, t('ak_fin2_edit'), () => setDrawer({ open: true, record: r }), !canEdit)}
                            {canEdit && r.status === 'draft' && iconBtn(Check, t('ak_fin2_activate'), () => patchStatus(r, 'active'), false, 'var(--ok)')}
                            {canEdit && r.status === 'active' && iconBtn(Archive, t('ak_fin2_complete'), () => patchStatus(r, 'completed'))}
                            {canEdit && r.status === 'draft' && (
                              confirming ? (
                                <button onClick={() => remove(r)} disabled={busyId != null}
                                  title={t('ak_fin2_confirm_draft_deletion')}
                                  className="text-[10.5px] px-2 inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
                                  style={{ height: 28, borderRadius: 8, color: 'var(--warn)',
                                           border: '1px solid var(--warn)',
                                           background: 'color-mix(in srgb, var(--warn) 14%, transparent)' }}>
                                  <TriangleAlert size={12} strokeWidth={2} /> {t('ak_fin2_confirm')}
                                </button>
                              ) : iconBtn(Trash2, t('ak_fin2_delete_draft'), () => setConfirmDel(r.id), false, 'var(--warn)')
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[11px] mt-2" style={{ color: 'var(--text-dim)' }}>
            {t('ak_fin2_the_database_enforces_the_rule_one_act')}
          </div>
        </div>
      </div>

      <SaleTermsDrawer
        open={drawer.open} record={drawer.record} assets={assets} canEdit={canEdit}
        onClose={() => setDrawer({ open: false, record: null })}
        onSaved={() => { setDrawer({ open: false, record: null }); setMsg({ kind: 'ok', text: t('ak_fin2_saved') }); load(); }}
      />
    </DashboardLayout>
  );
}
